import {
    Avatar,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Tooltip,
    Zoom,
} from "@mui/material";
import { DateTime } from "luxon";
import { Fragment, createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbGitCompare, TbLocation, TbUserPin } from "react-icons/tb";

import { DriverStatus } from "@/types/daily-status.types";
import { WeTrackVehicleData } from "@/types/wetrack.types";

import { AddressItem, NoMaxWidthTooltip } from "./DriverStatusColumnTooltip";

const TimeAddressItem = ({
    location,
    address,
    backgroundColor,
    title,
    icon,
}: {
    location: {
        type: "Point";
        coordinates: [number, number];
        crs: {
            type: string;
            properties: {
                name: string;
            };
        };
    };
    address: string;
    backgroundColor: string;
    title: string;
    icon: IconType;
}) => {
    const { t } = useTranslation();

    return (
        <ListItem
            secondaryAction={
                location?.coordinates && (
                    <Tooltip title={t("Haritalarda Göster")}>
                        <IconButton
                            component="a"
                            edge="end"
                            color="primary"
                            size="small"
                            aria-label="delete"
                            target="_blank"
                            href={`https://www.google.com/maps?q=${location.coordinates[1]},${location.coordinates[0]}`}
                        >
                            <TbLocation />
                        </IconButton>
                    </Tooltip>
                )
            }
        >
            <ListItemAvatar>
                <Avatar sx={{ backgroundColor }}>
                    {createElement(icon || TbUserPin, {
                        fontSize: 20,
                    })}
                </Avatar>
            </ListItemAvatar>
            <ListItemText primary={t(title || "")} secondary={<div className=" pr-2">{address}</div>} />
        </ListItem>
    );
};
const DriverStatusColumnTimeTooltip = ({
    time,
    item,
    list,
    title,
}: {
    title: string;
    list: AddressItem[];
    time?: DateTime;
    item: DriverStatus;
}) => {
    const { t } = useTranslation();
    const items = useMemo(() => {
        return list
            .map((cell) => {
                const address = item[cell.key as keyof DriverStatus] as string;
                const location = item[cell.coordinates as keyof DriverStatus] as WeTrackVehicleData["geometry"];

                if (!address || !location) return false;
                return {
                    location,
                    address,
                    backgroundColor: cell.backgroundColor,
                    title: cell.title,
                    icon: cell.icon,
                };
            })
            .filter((item) => !!item);
    }, [list, item]);
    const compareLink = useMemo(() => {
        if (items.length !== 2) return false;
        const [item1, item2] = items;
        if (!item1.location || !item2.location) return false;
        return `https://www.google.com/maps/dir/?api=1&origin=${item1.location.coordinates[1]},${item1.location.coordinates[0]}&destination=${item2.location.coordinates[1]},${item2.location.coordinates[0]}`;
    }, [items, item]);

    return items.length ? (
        <NoMaxWidthTooltip
            title={
                <div className=" text-base py-2 flex flex-col  min-w-80 gap-1 ">
                    <div className="flex justify-between gap-1">
                        <div className="flex items-center gap-1  pl-2">
                            <TbLocation /> {t(title || "Konum Bilgileri")}
                        </div>
                        {compareLink && (
                            <Button component="a" target="__blank" href={compareLink} startIcon={<TbGitCompare />}>
                                {t("buttons.compare")}
                            </Button>
                        )}
                    </div>
                    <Divider className="!bg-gray-200  !w-[80%] !mx-auto" />
                    <List dense sx={{ width: 450, padding: "0px !important" }}>
                        {items.map((cell) => (
                            <Fragment key={cell.address}>
                                <TimeAddressItem
                                    address={cell.address}
                                    backgroundColor={cell.backgroundColor}
                                    location={cell.location}
                                    title={cell.title}
                                    icon={cell.icon}
                                />
                                <Divider className="!bg-gray-200  !w-[80%] !mx-auto" />
                            </Fragment>
                        ))}
                    </List>
                </div>
            }
            slots={{
                transition: Zoom,
            }}
            arrow
        >
            <span>{(time && time.toFormat("dd.MM.yyyy HH:mm")) || t("continue")} </span>
        </NoMaxWidthTooltip>
    ) : (
        <span>{(time && time.toFormat("dd.MM.yyyy HH:mm")) || t("continue")} </span>
    );
};

export default DriverStatusColumnTimeTooltip;
